import React, { useState, useMemo } from 'react';
import { Row, Col, ButtonGroup, Input, Label, Form } from 'reactstrap';
import TableContainerReactTable from '../../../../Components/Common/TableContainerReactTable';
import FormInput from '../common/FormInput';
import SelectInput from '../common/SelectInput';

interface AddressData {
  id: string;
  addrLine1: string;
  addrLine2: string;
  addrLine3: string;
  zipPostCode: string;
  country: string;
  stateProvince: string;
  active: boolean;
  billing: boolean;
  shipping: boolean;
  primary: boolean;
  phone: string;
  fax: string;
}

interface AddressFormData {
  addrLine1: string;
  addrLine2: string;
  addrLine3: string;
  zipPostCode: string;
  country: string;
  stateProvince: string;
  active: boolean;
  billing: boolean;
  shipping: boolean;
  primary: boolean;
  phone: string;
  fax: string;
}

const AddressesTab = () => {
  const [addressForm, setAddressForm] = useState<AddressFormData>({
    addrLine1: '',
    addrLine2: '',
    addrLine3: '',
    zipPostCode: '',
    country: 'Australia',
    stateProvince: '',
    active: true,
    billing: false,
    shipping: false,
    primary: false,
    phone: '',
    fax: '',
  });

  const handleNew = () => {
    setAddressForm({
      addrLine1: '',
      addrLine2: '',
      addrLine3: '',
      zipPostCode: '',
      country: 'Australia',
      stateProvince: '',
      active: true,
      billing: false,
      shipping: false,
      primary: false,
      phone: '',
      fax: '',
    });
  };

  const handleSave = () => {
    console.log('Saving address...', addressForm);
  };

  const handleCancel = () => {
    console.log('Cancelling changes...');
    setAddressForm({
      addrLine1: '',
      addrLine2: '',
      addrLine3: '',
      zipPostCode: '',
      country: 'Australia',
      stateProvince: '',
      active: true,
      billing: false,
      shipping: false,
      primary: false,
      phone: '',
      fax: '',
    });
  };

  const handleDelete = () => {
    console.log('Deleting address...');
  };

  const handleInputChange = (field: keyof AddressFormData) => (value: string) => {
    setAddressForm({ ...addressForm, [field]: value });
  };

  const handleCheckboxChange = (field: keyof AddressFormData) => (checked: boolean) => {
    setAddressForm({ ...addressForm, [field]: checked });
  };

  // Sample data
  const data: AddressData[] = [
    {
      id: '1',
      addrLine1: '123 Queen Street',
      addrLine2: 'Level 5',
      addrLine3: 'Suite 502',
      zipPostCode: '1010',
      country: 'New Zealand',
      stateProvince: 'Auckland',
      active: true,
      billing: true,
      shipping: false,
      primary: true,
      phone: '+64 9 123 4567',
      fax: '+64 9 123 4568',
    },
    {
      id: '2',
      addrLine1: '456 Collins Street',
      addrLine2: '',
      addrLine3: '',
      zipPostCode: '3000',
      country: 'Australia',
      stateProvince: 'Victoria',
      active: true,
      billing: false,
      shipping: true,
      primary: false,
      phone: '+61 3 9876 5432',
      fax: '+61 3 9876 5433',
    },
    {
      id: '3',
      addrLine1: '789 George Street',
      addrLine2: 'Building B',
      addrLine3: '',
      zipPostCode: '2000',
      country: 'Australia',
      stateProvince: 'New South Wales',
      active: true,
      billing: true,
      shipping: true,
      primary: false,
      phone: '+61 2 5555 1234',
      fax: '+61 2 5555 1235',
    },
  ];

  // Column definitions
  const columns = useMemo(
    () => [
      { header: 'Addr Line 1', accessorKey: 'addrLine1', size: 150, enableColumnFilter: false },
      { header: 'Addr Line 2', accessorKey: 'addrLine2', size: 120, enableColumnFilter: false },
      { header: 'Addr Line 3', accessorKey: 'addrLine3', size: 120, enableColumnFilter: false },
      { header: 'Zip/Post Code', accessorKey: 'zipPostCode', size: 100, enableColumnFilter: false },
      { header: 'Country', accessorKey: 'country', size: 120, enableColumnFilter: false },
      { header: 'State/Province', accessorKey: 'stateProvince', size: 130, enableColumnFilter: false },
      {
        header: 'Active',
        accessorKey: 'active',
        size: 80,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() ? (
          <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle text-success"></i></div>
        ) : (
          <div className="text-center"><i className="ri-close-circle-line fs-17 align-middle text-danger"></i></div>
        ),
      },
      {
        header: 'Billing',
        accessorKey: 'billing',
        size: 80,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() ? (
          <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle text-success"></i></div>
        ) : null,
      },
      {
        header: 'Shipping',
        accessorKey: 'shipping',
        size: 80,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() ? (
          <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle text-success"></i></div>
        ) : null,
      },
      {
        header: 'Primary',
        accessorKey: 'primary',
        size: 80,
        enableColumnFilter: false,
        cell: (cell: any) => cell.getValue() ? (
          <div className="text-center"><i className="ri-checkbox-circle-line fs-17 align-middle text-primary"></i></div>
        ) : null,
      },
      { header: 'Phone', accessorKey: 'phone', size: 120, enableColumnFilter: false },
      { header: 'Fax', accessorKey: 'fax', size: 120, enableColumnFilter: false },
    ],
    []
  );

  const countryOptions = [
    'Australia',
    'New Zealand',
    'United States',
    'United Kingdom',
    'Canada',
    'Singapore',
    'Other'
  ];

  const stateProvinceOptions = [
    'Australian Capital Territory',
    'New South Wales',
    'Northern Territory',
    'Queensland',
    'South Australia',
    'Tasmania',
    'Victoria',
    'Western Australia',
    'Auckland',
    'Bay of Plenty',
    'Canterbury',
    'Gisborne',
    'Hawke\'s Bay',
    'Manawatu-Wanganui',
    'Marlborough',
    'Nelson',
    'Northland',
    'Otago',
    'Southland',
    'Taranaki',
    'Tasman',
    'Waikato',
    'Wellington',
    'West Coast'
  ];

  return (
    <div className="page-content p-0">
      {/* Action Buttons */}
      <Row className="mb-3">
        <Col>
          <ButtonGroup className='gap-2'>
            <button className="btn btn-soft-primary btn-sm" onClick={handleNew} style={{ minWidth: 80 }}>
              <i className="ri-add-line align-middle me-1"></i> New
            </button>
            <button className="btn btn-soft-success btn-sm" onClick={handleSave} style={{ minWidth: 80 }}>
              <i className="ri-save-line align-middle me-1"></i> Save
            </button>
            <button className="btn btn-soft-warning btn-sm" onClick={handleCancel} style={{ minWidth: 80 }}>
              <i className="ri-close-line align-middle me-1"></i> Cancel
            </button>
            <button className="btn btn-soft-danger btn-sm" onClick={handleDelete} style={{ minWidth: 80 }}>
              <i className="ri-delete-bin-line align-middle me-1"></i> Delete
            </button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Address Table */}
      <div className="address-content">
        <TableContainerReactTable
          columns={columns}
          data={data}
          divClass="table-responsive"
          tableClass="align-middle table-nowrap"
          theadClass="table-light"
        />
      </div>
    </div>
  );
};

export default AddressesTab; 